'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from '../lib';

/**
 * -----------------------------------------------------------------------------
 * A vertically stacked set of interactive headings that each reveal a section
 * of content.
 * - See more here - https://ui.shadcn.com/docs/components/accordion
 */
function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

/**
 * -----------------------------------------------------------------------------
 * This displays a single item of the accordion
 */
function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      className={cn('border-border/50 border-b last:border-b-0', className)}
      data-slot="accordion-item"
      {...props}
    />
  );
}

/**
 * -----------------------------------------------------------------------------
 * The component that triggers/opens the accordion
 */
function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          `
          focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1
          items-start justify-between gap-4 rounded-md py-4 text-left text-sm
          font-medium outline-none transition-all hover:underline
          focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50
          [&[data-state=open]>svg]:rotate-180`,
          className
        )}
        data-slot="accordion-trigger"
        {...props}
      >
        {children}
        <ChevronDownIcon
          className="
            text-muted-foreground size-5 pointer-events-none shrink-0 translate-y-0.5
            transition-transform duration-200
          "
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Displays the content of the accordion
 */
function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      className="
        data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down
        overflow-hidden text-sm
      "
      data-slot="accordion-content"
      {...props}
    >
      <div className={cn('pb-4 pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
